import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {Plate} from '../models-new/plate';
import {ArrayStatus, PlateStatus, RunStatus} from '../constants/Status';
import {MessagesNewService} from '../messages-new/messages.service';
import {PlateProcessingService} from '../plate-processing/plate-processing.service';
import {PlateType} from '../constants/PlateType';
import {TableRowSelectEvent} from 'primeng/table';
import {showLatestComment} from "../utils/latest-comment-util";
import { formatDate,DatePipe } from '@angular/common';
import { Batch } from '../models-new/batch';
import { MasterDataService } from '../masterdata/masterdata.service';
import { Project } from '../masterdata/models/project';
import { ProjectWorkflow } from '../masterdata/models/projectWorkflow';
import { PlateListDTO } from '../dto/plateListDTO';
import { Run } from '../models/run';
import { MachineNewService } from '../machines-new/machines-new.service';

@Component({
  selector: "app-plate-list",
  templateUrl: "./plate-list.component.html",
  styleUrls: ["./plate-list.component.css"],
})
export class PlateListComponent implements OnInit, OnChanges {
  @Input() plateTableVisible = false;
  @Input() availableStatuses: Array<any> = [];
  @Input() availableBatchStatuses: Array<any> = [];
  @Input() availableArrayStatuses: Array<any> = [];
  @Input() availableRunStatuses: Array<any> = [];
  @Input() selectedPlate = new Plate();
  @Input() showDateFilter = false;
  @Input() showProtocolFilter = false;
  @Input() loadExtractionProtocols = false;

  readonly showLatestComment = showLatestComment;

  selectedLabId: string;
  platesTableLoader = false;
  plates: Array<Plate> = [];
  selectedStatuses: Array<any> = [];

  batchesTableLoader = false;
  batches: Array<Batch> = [];
  selectedBatchStatuses: Array<any> = [];
  arraysTableLoader = false;
  arrays: Array<Plate> = [];
  selectedArrayStatuses: Array<any> = [];

  projectList: Array<any> = [];
  selectedProjects: string[] = [];

  protocolList: Array<any> = [];
  selectedProtocolWorkflows: string[] = [];

  toDate = new Date();
  fromDate = new Date();

  showDueDate = false;
  runs: Run[] = [];
  excludedStatuses: Array<string> = [
    PlateStatus.STORAGE_PLATE,
    PlateStatus.COMPLETELY_TRANSFERRED,
    ArrayStatus.DELIVERED,
  ];

  @Output() fetchPlateEmitter = new EventEmitter<string>();
  @Output() fetchBatchEmitter = new EventEmitter<string>();
  @Output() removeSelectionEmitter = new EventEmitter();
  @Output() fetchRunEmitter = new EventEmitter();

  constructor(
    private readonly messagesService: MessagesNewService,
    private readonly plateProcessingService: PlateProcessingService,
    private readonly masterDataService: MasterDataService,
    private readonly datePipe: DatePipe,
    private readonly machineNewService: MachineNewService
  ) {}

  ngOnInit() {
    const selectedLab = JSON.parse(localStorage.getItem("lab") || '{"lab":""}');
    this.selectedLabId = selectedLab.lab._id;
    if (selectedLab.lab.dateRange) {
      this.fromDate.setDate(this.toDate.getDate() - 5);
    } else {
      this.fromDate.setDate(this.toDate.getDate() - 31);
    }
    this.populateProjectList();
    this.populateProtocolList();
    this.populateAllLists();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.plateTableVisible && this.plateTableVisible) {
      this.populateAllLists();
    }
  }

  populateAllLists() {
    if (this.showPlatesTable()) {
      this.getPlateList();
    }
    if (this.showArraysTable()) {
      this.getArrayList();
    }
    if (this.showBatchesTable()) {
      this.getBatchList();
    }
    if (this.showRunTable()) {
      this.fetchRuns();
    }
  }

  showArraysTable() {
    return this.availableArrayStatuses.length !== 0;
  }

  showBatchesTable() {
    return this.availableBatchStatuses.length !== 0;
  }

  showRunTable() {
    return this.availableRunStatuses.length !== 0;
  }

  populateProjectList() {
    this.masterDataService
      .getConfigList(this.selectedLabId, "project", 0, 1000, true)
      .subscribe({
        next: (results) => {
          if (!results) {
            return;
          }
          const activeProjectsList = results.data;
          activeProjectsList.forEach((project: Project) => {
            this.projectList.push({
              label: project.projectName,
              value: project.projectId,
            });
          });
        },
        error: (error) => {
          this.messagesService.addErrorMessage({
            severity: "error",
            summary: "Error while fetching active projects",
            detail: error.error.bbApiError.message,
          });
        },
      });
  }

  populateProtocolList() {
    this.masterDataService
      .getConfigList(this.selectedLabId, "projectWorkflow", 0, 1000, true)
      .subscribe({
        next: (results) => {
          if (!results) {
            return;
          }
          const activeProjectWorkflowsList = results.data;
          const loadedProtocols = new Set<string>();
          activeProjectWorkflowsList.forEach((workflow: ProjectWorkflow) => {
            if (this.loadExtractionProtocols) {
              if (workflow.upstream.length > 0) {
                const uniqueKey = `${workflow.upstream[0].protocolName}-${workflow.upstream[0].protocolVersion}`;
                if (!loadedProtocols.has(uniqueKey)) {
                  this.protocolList.push({
                    label: workflow.upstream[0].protocolName,
                    value: [workflow.workflowName],
                  });
                  loadedProtocols.add(uniqueKey);
                } else {
                  const existingProtocol = this.protocolList.find(
                    (protocol) =>
                      protocol.label === workflow.upstream[0].protocolName
                  );
                  existingProtocol.value.push(workflow.workflowName);
                }
              }
            } else if (workflow.downstream.length > 0) {
              const uniqueKey = `${workflow.downstream[0].protocolName}-${workflow.downstream[0].protocolVersion}`;
              if (!loadedProtocols.has(uniqueKey)) {
                this.protocolList.push({
                  label: workflow.downstream[0].protocolName,
                  value: [workflow.workflowName],
                });
                loadedProtocols.add(uniqueKey);
              } else {
                const existingProtocol = this.protocolList.find(
                  (protocol) =>
                    protocol.label === workflow.downstream[0].protocolName
                );
                existingProtocol.value.push(workflow.workflowName);
              }
            }
          });
        },
        error: (error) => {
          this.messagesService.addErrorMessage({
            severity: "error",
            summary: "Error while fetching active project workflows",
            detail: error.error.bbApiError.message,
          });
        },
      });
  }

  showPlatesTable() {
    return this.availableStatuses.length !== 0;
  }

  getArrayListDivClasses() {
    if (!this.showArraysTable()) {
      return "hidden";
    }

    if (this.showPlatesTable()) {
      return "height-45";
    }

    return "";
  }

  getBatchListDivClasses() {
    if (!this.showBatchesTable()) {
      return "hidden";
    }

    if (this.showBatchesTable()) {
      return "height-45";
    }

    return "";
  }

  getPlateListDivClasses() {
    if (!this.showPlatesTable()) {
      return "hidden";
    }

    if (this.showArraysTable()) {
      return "height-45";
    }

    return "";
  }

  preparePlateSearchDTO(
    labId: string,
    type: string,
    status?: string,
    fromDate?: Date,
    toDate?: Date,
    selectedProjects?: string,
    selectedProtocolWorkflows?: string
  ): PlateListDTO {
    const plateListDTO = new PlateListDTO();
    plateListDTO.labId = labId;
    if (typeof type !== "undefined") {
      plateListDTO.type = type;
    }
    if (typeof status !== "undefined") {
      plateListDTO.status = status;
    }
    if (fromDate !== undefined && fromDate !== null) {
      plateListDTO.fromDate = formatDate(fromDate, "yyyy-MM-dd", "en-US");
    }
    if (toDate !== undefined && toDate !== null) {
      plateListDTO.toDate = formatDate(toDate, "yyyy-MM-dd", "en-US");
    }
    if (
      typeof selectedProjects !== "undefined" &&
      selectedProjects.trim() !== ""
    ) {
      plateListDTO.selectedProjects = selectedProjects;
    }
    if (
      typeof selectedProtocolWorkflows !== "undefined" &&
      selectedProtocolWorkflows.trim() !== ""
    ) {
      plateListDTO.selectedProtocolWorkflows = selectedProtocolWorkflows;
    }
    return plateListDTO;
  }

  getPlateList() {
    let statuses: string[];
    if (!this.selectedStatuses || this.selectedStatuses.length === 0) {
      statuses = Object.keys(PlateStatus);
      statuses = statuses.filter(
        (status) => !this.excludedStatuses.includes(status)
      );
    } else {
      statuses = this.selectedStatuses.map((statusObj) => statusObj["value"]);
    }
    this.platesTableLoader = true;
    const plateListDTO = this.preparePlateSearchDTO(
      this.selectedLabId,
      PlateType.PLATE,
      statuses.join(","),
      this.fromDate,
      this.toDate,
      this.selectedProjects?.join(","),
      this.selectedProtocolWorkflows?.join(",")
    );
    this.plateProcessingService.getPlates(plateListDTO).subscribe({
      next: (result) => {
        this.platesTableLoader = false;
        this.plates = result.data;
      },
      error: (error) => {
        this.platesTableLoader = false;
        this.messagesService.addErrorMessage({
          severity: "error",
          summary: "Error while fetching plates list",
          detail: error.error.bbApiError.message,
        });
      },
    });
  }

  fetchRuns() {
    let statuses: string[];
    if (!this.selectedStatuses || this.selectedStatuses.length === 0) {
      statuses = Object.keys(RunStatus);
    } else {
      statuses = this.selectedStatuses.map((statusObj) => statusObj["value"]);
    }
    this.machineNewService
      .fetchRuns(
        this.selectedLabId,
        statuses.join(","),
        this.fromDate,
        this.toDate,
        this.selectedProjects?.join(",")
      )
      .subscribe({
        next: (result) => {
          this.runs = result.data;
        },
        error: (_error) => {
          this.messagesService.addErrorMessage({
            severity: "error",
            summary: "Failed to fetch runs",
            detail: "Some issue in pulling the runs info, please try again",
          });
        },
      });
  }

  getArrayList() {
    let statuses: string[];
    if (this.selectedArrayStatuses?.length === 0) {
      statuses = Object.keys(ArrayStatus);
    } else {
      statuses = this.selectedArrayStatuses.map(
        (statusObj) => statusObj["value"]
      );
    }

    this.arraysTableLoader = true;
    const plateListDTO = this.preparePlateSearchDTO(
      this.selectedLabId,
      PlateType.ARRAY,
      statuses.join(","),
      this.fromDate,
      this.toDate,
      this.selectedProjects?.join(","),
      this.selectedProtocolWorkflows?.join(",")
    );
    this.plateProcessingService.getPlates(plateListDTO).subscribe({
      next: (result) => {
        this.arraysTableLoader = false;
        this.arrays = result.data;
      },
      error: (error) => {
        this.arraysTableLoader = false;
        this.messagesService.addErrorMessage({
          severity: "error",
          summary: "Error while fetching array list",
          detail: error.error.bbApiError.message,
        });
      },
    });
  }

  getBatchList() {
    const statuses =
      this.selectedBatchStatuses?.length === 0
        ? this.availableBatchStatuses.map((status) => status["value"] as string)
        : this.selectedBatchStatuses?.map(
            (status) => status["value"] as string
          );
    this.arraysTableLoader = true;
    const plateListDTO = this.preparePlateSearchDTO(
      this.selectedLabId,
      PlateType.PLATE_BATCH,
      statuses.join(","),
      this.fromDate,
      this.toDate,
      this.selectedProjects?.join(","),
      this.selectedProtocolWorkflows?.join(",")
    );
    this.plateProcessingService.getPlates(plateListDTO).subscribe({
      next: (result) => {
        this.batchesTableLoader = false;
        this.batches = result.data;
      },
      error: (error) => {
        this.arraysTableLoader = false;
        this.messagesService.addErrorMessage({
          severity: "error",
          summary: "Error while fetching batch list",
          detail: error.error.bbApiError.message,
        });
      },
    });
  }

  fetchPlate(event: TableRowSelectEvent) {
    const batchID = event.data.batchID;
    if (batchID) {
      this.messagesService.addSuccessMessage({
        severity: "success",
        summary: "Plate is on a batch!",
        detail: `Plate ${event.data.lastUpdatedPlateID} is on a batch. Loading batch: ${batchID}`,
      });
      this.fetchBatchEmitter.emit(batchID);
      return;
    }
    this.fetchPlateEmitter.emit(event.data.plateID.trim());
  }

  fetchBatch(event: TableRowSelectEvent) {
    this.fetchBatchEmitter.emit(event.data.batchID.trim());
  }

  removeSelection() {
    this.removeSelectionEmitter.emit();
  }

  fetchRun(event: TableRowSelectEvent) {
    this.fetchRunEmitter.emit(event.data.runId.trim());
  }

  getTableScrollHeight() {
    if (
      (this.showArraysTable() && this.showPlatesTable()) ||
      this.showBatchesTable()
    ) {
      return "30vh";
    }
    return "60vh";
  }

  convertUtcToLocal(date: Date) {
    if (!date) {
      return null;
    }
    const offset = new Date().getTimezoneOffset();
    const dateInLocal = new Date(date);
    dateInLocal.setMinutes(dateInLocal.getMinutes() - offset);
    return dateInLocal;
  }

  checkStatusChange(plate: Plate): boolean {
    const { lastModifiedDate, adminChangeTimeStamp, redoInformation } = plate;
    if (lastModifiedDate === adminChangeTimeStamp) {
      return true;
    }
    const redoDoneOn = redoInformation?.redoDoneOn;
    return (
      redoDoneOn && adminChangeTimeStamp && redoDoneOn === adminChangeTimeStamp
    );
  }
}
